import { useMemo } from 'react';
import { useCheckPermission } from '../useUserRoles';
import { useProjectPermissions } from '../useProjectPermissions';
import { useAdminCheck } from '../useAdminCheck';

/**
 * Item Milestones Permission Hook
 * 
 * 2-Layer Permission System:
 * - Layer 1 (Global): ITEMS_VIEW/EDIT/DELETE_MILESTONES
 * - Layer 2 (Project): can_view/edit/delete
 * 
 * Both layers required (AND logic)
 * Feature Flag: ENABLE_CUSTOM_MILESTONES
 */
export const useItemMilestonePermissions = (projectId?: string) => {
  const { isAdmin } = useAdminCheck();
  
  // Layer 1: Global permissions
  const { data: hasViewPermission = false } = useCheckPermission('ITEMS_VIEW_MILESTONES');
  const { data: hasEditPermission = false } = useCheckPermission('ITEMS_EDIT_MILESTONES');
  const { data: hasDeletePermission = false } = useCheckPermission('ITEMS_DELETE_MILESTONES');
  
  // Layer 2: Project permissions
  const { data: projectPerms } = useProjectPermissions(projectId || '');

  return useMemo(() => {
    // Admin bypass
    if (isAdmin) {
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
      };
    }

    // AND logic: Both layers must pass
    const canView = hasViewPermission && (projectPerms?.can_view ?? false);
    const canEdit = hasEditPermission && (projectPerms?.can_edit ?? false);
    const canDelete = hasDeletePermission && (projectPerms?.can_delete ?? false);

    return {
      canView,
      canCreate: canEdit,
      canEdit: canEdit,
      canDelete: canDelete,
    };
  }, [isAdmin, hasViewPermission, hasEditPermission, hasDeletePermission, projectPerms]);
};
